<script setup>
/* global axios */
// Agent formunu API dokumanindan doldurur.
//
// NEDEN: musteri "REST API'm var" diyor ama endpoint, Liquid sablonu,
// parametre semasi gibi kavramlari bilmiyor. Dokumani ve kendi adresini
// verip ne istedigini Turkce yaziyor; gerisini model yapiyor.
//
// KIMLIK BILGISI SUNUCUYA GITMEZ. Kullanici adi ve parola yalnizca bu
// ekranda durur, dogrudan formun kimlik alanina yazilir. Modele giden
// istekte yer almazlar; model sablona {{ omnup_kullanici }} /
// {{ omnup_parola }} koyar, gercek degerler istek aninda yerine gecer.
import { computed, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['doldur']);

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

// Urun bilgisi icin dogru yer katalog. Musteriyi oraya goturuyoruz ki
// "nereden yapacagim" diye aramasin.
const kataloga = () => {
  router.push({
    name: 'katalog_api',
    params: { accountId: route.params.accountId },
  });
};

const YOKLAMA_MS = 2500;

const taban = computed(
  () => `/api/v1/accounts/${route.params.accountId}/agent-sihirbazi`
);

const acik = ref(false);
const kaynak = ref('');
const niyet = ref('');
const temelAdres = ref('');
const kullanici = ref('');
const parola = ref('');
const dosya = ref(null);
const dosyaAdi = ref('');

const calisiyor = ref(false);
const asama = ref('');
const hata = ref('');
const dosyaIste = ref(false);

const oturum = ref('');
const uclar = ref([]);
const yapili = ref(false);
const seciliUc = ref(null);
const sonuc = ref(null);

let zamanlayici = null;

const durdur = () => {
  if (zamanlayici) clearTimeout(zamanlayici);
  zamanlayici = null;
};

onUnmounted(durdur);

const secilebilir = computed(
  () => Boolean(oturum.value) && yapili.value && uclar.value.length > 1
);
const baslatilabilir = computed(
  () => Boolean(kaynak.value.trim()) || Boolean(dosya.value)
);

const dosyaSecildi = olay => {
  const secilen = olay.target.files && olay.target.files[0];
  dosya.value = secilen || null;
  dosyaAdi.value = secilen ? secilen.name : '';
};

// Is bitene kadar yoklar; sonucu cozumleyerek dondurur.
const isiBekle = isId =>
  new Promise((coz, reddet) => {
    const bak = async () => {
      try {
        const { data } = await axios.get(`${taban.value}/${isId}`);
        if (data.durum === 'calisiyor') {
          zamanlayici = setTimeout(bak, YOKLAMA_MS);
          return;
        }
        if (data.durum === 'hazir') coz(data.sonuc);
        else reddet(new Error(data.hata || t('OMNUP_SIHIRBAZ.HATA')));
      } catch (e) {
        reddet(new Error(e?.response?.data?.error || t('OMNUP_SIHIRBAZ.HATA')));
      }
    };
    zamanlayici = setTimeout(bak, YOKLAMA_MS);
  });

const doldur = async () => {
  calisiyor.value = true;
  asama.value = 'doldur';
  hata.value = '';
  try {
    const { data } = await axios.post(`${taban.value}/doldur`, {
      oturum: oturum.value,
      uc_index: seciliUc.value,
      niyet: niyet.value,
    });
    const cikti = await isiBekle(data.is_id);
    sonuc.value = cikti.form;

    // Kimlik degerlerini forma BURADA ekliyoruz; sunucuya hic gitmediler.
    emit('doldur', {
      ...cikti.form,
      kimlik_degerleri: {
        username: kullanici.value,
        password: parola.value,
      },
    });
  } catch (e) {
    hata.value = e.message || t('OMNUP_SIHIRBAZ.HATA');
  } finally {
    calisiyor.value = false;
    asama.value = '';
  }
};

const basla = async () => {
  if (!baslatilabilir.value) return;

  durdur();
  calisiyor.value = true;
  asama.value = 'incele';
  hata.value = '';
  dosyaIste.value = false;
  oturum.value = '';
  uclar.value = [];
  yapili.value = false;
  seciliUc.value = null;
  sonuc.value = null;

  try {
    // Dosya varsa multipart, yoksa duz JSON. Kimlik alanlari HICBIRINDE yok.
    let istek;
    if (dosya.value) {
      const govde = new FormData();
      govde.append('dosya', dosya.value);
      govde.append('kaynak', kaynak.value);
      govde.append('niyet', niyet.value);
      govde.append('temel_adres', temelAdres.value);
      istek = axios.post(`${taban.value}/kesfet`, govde);
    } else {
      istek = axios.post(`${taban.value}/kesfet`, {
        kaynak: kaynak.value,
        niyet: niyet.value,
        temel_adres: temelAdres.value,
      });
    }

    const { data } = await istek;
    const cikti = await isiBekle(data.is_id);

    oturum.value = cikti.oturum;
    yapili.value = Boolean(cikti.yapili);
    uclar.value = cikti.uclar || [];
    seciliUc.value = uclar.value.length ? uclar.value[0].index : null;

    // Tek adimda bitiyor: modelin en iyi secimiyle formu dolduruyoruz.
    // Musteri isterse asagidan baska bir islem secip tekrar doldurabilir.
    await doldur();
  } catch (e) {
    calisiyor.value = false;
    asama.value = '';
    hata.value = e.message || t('OMNUP_SIHIRBAZ.HATA');
    // Dokumana ulasilamadiysa cozum dosya yuklemek; ekranda soyluyoruz.
    dosyaIste.value = !dosya.value;
  }
};
</script>

<template>
  <div class="rounded-xl border border-solid border-n-weak bg-n-alpha-1">
    <button
      type="button"
      class="flex items-center justify-between w-full gap-3 px-4 py-3 bg-transparent border-0 cursor-pointer"
      @click="acik = !acik"
    >
      <span class="flex items-center gap-2 min-w-0">
        <span class="i-lucide-sparkles size-4 text-n-brand shrink-0" />
        <span class="flex flex-col items-start min-w-0">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('OMNUP_SIHIRBAZ.BASLIK') }}
          </span>
          <span class="text-xs text-start text-n-slate-11">
            {{ t('OMNUP_SIHIRBAZ.ALT_BASLIK') }}
          </span>
        </span>
      </span>
      <span
        class="size-4 shrink-0 text-n-slate-11"
        :class="acik ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
      />
    </button>

    <div v-if="acik" class="flex flex-col gap-3 px-4 pb-4">
      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('OMNUP_SIHIRBAZ.KAYNAK') }}
        </span>
        <textarea
          v-model="kaynak"
          rows="2"
          class="!mb-0 text-sm"
          :placeholder="t('OMNUP_SIHIRBAZ.KAYNAK_IPUCU')"
        />
        <span class="text-xs text-n-slate-10">
          {{ t('OMNUP_SIHIRBAZ.KAYNAK_NOT') }}
        </span>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('OMNUP_SIHIRBAZ.DOSYA') }}
        </span>
        <input
          type="file"
          class="!mb-0 text-sm"
          accept=".pdf,.json,.yaml,.yml,.txt,.md,.csv"
          @change="dosyaSecildi"
        />
        <span class="text-xs text-n-slate-10">
          {{ dosyaAdi || t('OMNUP_SIHIRBAZ.DOSYA_NOT') }}
        </span>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('OMNUP_SIHIRBAZ.TEMEL_ADRES') }}
        </span>
        <input
          v-model="temelAdres"
          type="text"
          class="!mb-0 text-sm"
          placeholder="https://magazam.butiksistem.com"
        />
        <span class="text-xs text-n-slate-10">
          {{ t('OMNUP_SIHIRBAZ.TEMEL_ADRES_NOT') }}
        </span>
      </label>

      <!-- Kimlik bilgileri sunucuya gonderilmiyor; dogrudan formun
           kimlik alanina yaziliyor. -->
      <div class="flex flex-wrap gap-2">
        <label class="flex flex-col flex-1 gap-1 min-w-48">
          <span class="text-xs font-medium text-n-slate-12">
            {{ t('OMNUP_SIHIRBAZ.KULLANICI') }}
          </span>
          <input v-model="kullanici" type="text" class="!mb-0 text-sm" />
        </label>
        <label class="flex flex-col flex-1 gap-1 min-w-48">
          <span class="text-xs font-medium text-n-slate-12">
            {{ t('OMNUP_SIHIRBAZ.PAROLA') }}
          </span>
          <input v-model="parola" type="password" class="!mb-0 text-sm" />
        </label>
      </div>
      <p class="text-xs text-n-slate-10">
        {{ t('OMNUP_SIHIRBAZ.KIMLIK_NOT') }}
      </p>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-n-slate-12">
          {{ t('OMNUP_SIHIRBAZ.NIYET') }}
        </span>
        <input
          v-model="niyet"
          type="text"
          class="!mb-0 text-sm"
          :placeholder="t('OMNUP_SIHIRBAZ.NIYET_IPUCU')"
        />
      </label>

      <div class="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="solid"
          :label="t('OMNUP_SIHIRBAZ.BASLA')"
          :is-loading="calisiyor"
          :disabled="calisiyor || !baslatilabilir"
          @click="basla"
        />
        <span
          v-if="calisiyor"
          class="flex items-center gap-2 text-xs text-n-slate-11"
        >
          <Spinner class="text-n-brand size-4" />
          {{
            asama === 'incele'
              ? t('OMNUP_SIHIRBAZ.INCELENIYOR')
              : t('OMNUP_SIHIRBAZ.DOLDURULUYOR')
          }}
        </span>
      </div>

      <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>
      <p
        v-if="dosyaIste"
        class="px-3 py-2 text-xs rounded-lg bg-n-amber-9/10 text-n-amber-11"
      >
        {{ t('OMNUP_SIHIRBAZ.DOSYA_ISTE') }}
      </p>

      <!-- Dokumanda birden fazla islem varsa baskasina gecebilsin. -->
      <template v-if="secilebilir">
        <div class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-12">
            {{ t('OMNUP_SIHIRBAZ.UC_SEC') }}
          </span>
          <div class="flex flex-wrap items-center gap-2">
            <select v-model="seciliUc" class="!mb-0 flex-1 min-w-64 text-sm">
              <option v-for="uc in uclar" :key="uc.index" :value="uc.index">
                {{ uc.yontem }} — {{ uc.ad }}
              </option>
            </select>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              :label="t('OMNUP_SIHIRBAZ.YENIDEN')"
              :disabled="calisiyor || seciliUc === null"
              @click="doldur"
            />
          </div>
        </div>
      </template>

      <div v-if="sonuc" class="flex flex-col gap-2">
        <!-- Urun bilgisi icin agent yanlis arac: cogu e-ticaret API'si
             musterinin kullandigi kelimelerle arama yapmiyor ve yuzlerce
             urun isteme sigmiyor. Model bunu gorunce kataloga yonlendiriyor. -->
        <div
          v-if="sonuc.katalog_onerisi"
          class="flex flex-col gap-2 px-3 py-3 rounded-lg bg-n-blue-9/10 text-n-blue-11"
        >
          <span class="flex items-center gap-2 text-sm font-medium">
            <span class="i-lucide-lightbulb size-4 shrink-0" />
            {{ t('OMNUP_SIHIRBAZ.KATALOG_BASLIK') }}
          </span>
          <span class="text-xs">{{ sonuc.katalog_onerisi }}</span>
          <div>
            <Button
              type="button"
              size="sm"
              variant="solid"
              :label="t('OMNUP_SIHIRBAZ.KATALOGA_GIT')"
              @click="kataloga"
            />
          </div>
        </div>

        <p class="px-3 py-2 text-xs rounded-lg bg-n-teal-9/10 text-n-teal-11">
          {{ t('OMNUP_SIHIRBAZ.DOLDURULDU') }}
        </p>

        <div
          v-if="sonuc.kimlik_ipucu"
          class="px-3 py-2 text-xs rounded-lg bg-n-amber-9/10 text-n-amber-11"
        >
          <strong>{{ t('OMNUP_SIHIRBAZ.KIMLIK') }}</strong>
          {{ sonuc.kimlik_ipucu }}
        </div>

        <ul
          v-if="sonuc.notlar && sonuc.notlar.length"
          class="flex flex-col gap-1 pl-4 m-0 text-xs list-disc text-n-slate-11"
        >
          <li v-for="(not, i) in sonuc.notlar" :key="i">{{ not }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>
